import React from 'react'
import {
  ButtonHelpToLogin,
  ButtonLogin,
  ButtonBack,
  Container,
  ContainerLoginForm,
  Division,
  InputLogin,
  ResetAccountAnchor,
  LoginWith,
  OtherLoginMethodsAnchor,
  WelcomeText
} from './LoginArea.styles'
import type { LoginAreaProps } from '../DefaultLogin.types'
import { HELP_TO_ACCESS_PATH } from 'components/help-to-access'
import { useTranslation } from 'i18n'
import ExternalAuthOptions from '../external-auth'
import { useToggle } from 'usehooks-ts'
import MoreLoginOptions from 'components/more-login-options'
import { RECOVER_ACCESS_PATH } from 'routes/paths'
import useA11yEnabled from 'utils/hooks/use-a11y-enabled'

const LoginArea: React.FC<LoginAreaProps> = ({
  loginRequiresPassword,
  userName,
  loginValue,
  passwordValue,
  loading,
  loginError,
  passwordError,
  handleLoginChange,
  handlePasswordChange,
  handleLoginSubmit,
  handlePasswordSubmit,
  handleNotMe
}) => {
  const [isOtherLoginMethodsAreaOpen, toggleOtherLoginMethodsArea] =
    useToggle(false)
  const { t } = useTranslation()
  const { a11yEnabled } = useA11yEnabled()

  const handleKeyUp: React.KeyboardEventHandler<HTMLInputElement> = ({
    key
  }) => {
    if (key === 'Enter')
      loginRequiresPassword ? handlePasswordSubmit() : handleLoginSubmit()
  }

  const errorMessages = {
    wrongPassword: t('Senha incorreta. Tente novamente.'),
    recaptchaFailed:
      'Verificação de segurança falhou. Por favor, recarregue a página e tente novamente.',
    general: t(
      'É necessário fazer atualizações no seu login. Entre em contato com o time de atendimento da Árvore: atendimento@arvore.com.br'
    )
  }

  const getErrorMessage = (error: string) => {
    switch (error) {
      case 'root_blocked_access_tokens':
        return t(
          'Tipo de login inválido. Entre em contato com o time de atendimento em atendimento@arvore.com.br'
        )
      case 'recaptcha_failed':
        return 'Verificação de segurança falhou. Por favor, recarregue a página e tente novamente.'
      default:
        return t('Esse usuário não foi encontrado')
    }
  }

  return (
    <Container isOtherLoginMethodsAreaOpen={isOtherLoginMethodsAreaOpen}>
      {isOtherLoginMethodsAreaOpen && (
        <MoreLoginOptions closeSection={toggleOtherLoginMethodsArea} />
      )}

      {!isOtherLoginMethodsAreaOpen && (
        <>
          {loginRequiresPassword && (
            <ButtonBack
              onClick={handleNotMe}
              iconName="arrow-left"
              isLeft
              theme={{ type: 'ghost', size: 'medium' }}
              text={t('Voltar')}
            />
          )}
          <WelcomeText>
            {userName ? `Oi, ${userName.split(' ')[0]} 👋` : 'Boas-vindas'}
          </WelcomeText>

          <ContainerLoginForm>
            {!loginRequiresPassword && (
              <>
                <InputLogin
                  autoFocus
                  id={'login'}
                  data-testid={'login-input'}
                  type={'text'}
                  label={t('Insira seu código de acesso ou e-mail')}
                  value={loginValue}
                  onChange={handleLoginChange}
                  onKeyUp={handleKeyUp}
                  error={Boolean(loginError)}
                  helpText={loginError && getErrorMessage(loginError)}
                  autoComplete="off"
                  aria-invalid={loginError ? true : undefined}
                  aria-describedby={loginError ? 'login-error' : undefined}
                />
                <span id="login-error" role="alert" className="sr-only">
                  {loginError ? getErrorMessage(loginError) : ''}
                </span>
              </>
            )}
            {loginRequiresPassword && (
              <>
                <InputLogin
                  autoFocus
                  id={'password'}
                  data-testid={'password-input'}
                  type={'password'}
                  label={t('Insira sua senha')}
                  value={passwordValue}
                  onChange={handlePasswordChange}
                  onKeyUp={handleKeyUp}
                  error={!!passwordError}
                  helpText={passwordError && errorMessages[passwordError]}
                  aria-invalid={passwordError ? true : undefined}
                  aria-describedby={
                    passwordError ? 'password-error' : undefined
                  }
                />
                <span id="password-error" role="alert" className="sr-only">
                  {passwordError ? errorMessages[passwordError] : ''}
                </span>
              </>
            )}
            <ButtonLogin
              text={t('Entrar')}
              loading={loading}
              disabled={loginError || passwordError}
              data-testid="submit-login"
              onClick={
                loginRequiresPassword ? handlePasswordSubmit : handleLoginSubmit
              }
            />

            <ResetAccountAnchor href={RECOVER_ACCESS_PATH} target="_blank">
              {t('Esqueci como entrar')}
            </ResetAccountAnchor>

            <Division a11yEnabled={a11yEnabled}>{t('ou')}</Division>

            <LoginWith a11yEnabled={a11yEnabled}>
              {t('Fazer login com:')}
            </LoginWith>
            <ExternalAuthOptions />
            <OtherLoginMethodsAnchor onClick={toggleOtherLoginMethodsArea}>
              Outros métodos de acesso
            </OtherLoginMethodsAnchor>
          </ContainerLoginForm>
        </>
      )}
      <ButtonHelpToLogin href={HELP_TO_ACCESS_PATH} target="_blank">
        {t('Precisa de ajuda para acessar?')}
      </ButtonHelpToLogin>
    </Container>
  )
}

export default LoginArea
